import { api } from './client';
import type { ImportResult } from './items';

/** A SIS store is a customer (Party of type 'MBO & SiS'). */
export interface SisStore {
  id: number;
  name: string;
  city: string;
  group: string;
}

export interface SisStockRow {
  partyId: number;
  storeName: string;
  storeCity: string;
  storeGroup: string;
  itemId: number;
  itemCode: string;
  itemBarcode: string;
  itemName: string;
  imageUrl?: string | null;
  unit: string;
  category: string | null;
  qty: number;
  reorderLevel: number;
  stockStatus: 'In Stock' | 'Low Stock' | 'Out of Stock';
  stockValue: number;
  lastUpdatedAt: string;
}

export interface SisLedgerRow {
  id: string;
  txnDate: string;
  txnType: string;
  refDoc: string | null;
  partyId: number;
  storeName: string;
  storeGroup: string;
  itemId: number;
  itemCode: string;
  itemName: string;
  unit: string;
  qtyIn: number;
  qtyOut: number;
  balance: number;
  /** Rate the stock was invoiced to the store at (see backend ledgerRates). */
  rate: number;
  /** (qty in + qty out) × rate. */
  value: number;
  remarks: string | null;
}

export async function listSisStores(): Promise<SisStore[]> {
  const r = await api.get<SisStore[]>('/sis-stock/stores');
  return r.data;
}

export async function listSisStock(filter: {
  partyId?: number;
  search?: string;
} = {}): Promise<SisStockRow[]> {
  const r = await api.get<SisStockRow[]>('/sis-stock', { params: filter });
  return r.data;
}

/** On-hand stock for one (store, item), split by the invoice it was issued on. */
export interface SisStockLot {
  id: number;
  invoiceNo: string;
  rate: number;
  qtyIn: number;
  qtyRemaining: number;
  lotDate: string;
  value: number;
}

export async function listSisStockLots(
  partyId: number,
  itemId: number,
): Promise<SisStockLot[]> {
  const r = await api.get<SisStockLot[]>('/sis-stock/lots', { params: { partyId, itemId } });
  return r.data;
}

/** One line of the daily sales feed (SisSaleTxn) — commercial detail + CN/DN drivers. */
export interface SisSaleRow {
  id: string;
  saleDate: string;
  txnId: string;
  partyId: number;
  storeName: string;
  storeCity: string;
  storeGroup: string;
  itemId: number;
  itemCode: string;
  itemBarcode: string;
  itemName: string;
  unit: string;
  qty: number;
  mrp: number;
  sellPrice: number;
  discount: number;
  isReturn: boolean;
  returnFromSis: boolean;
  netValue: number;
  taxAmount: number;
  srcInvoice: string;
  costRate: number | null;
  rateVariance: number | null;
  batchNo: string;
}

export async function listSisSales(filter: {
  partyId?: number;
  group?: string;
  from?: string;
  to?: string;
  search?: string;
} = {}): Promise<SisSaleRow[]> {
  const r = await api.get<SisSaleRow[]>('/sis-stock/sales', { params: filter });
  return r.data;
}

export async function listSisLedger(filter: {
  partyId?: number;
  itemId?: number;
  txnType?: string;
  from?: string;
  to?: string;
} = {}): Promise<SisLedgerRow[]> {
  const r = await api.get<SisLedgerRow[]>('/sis-stock/ledger', { params: filter });
  return r.data;
}

export interface SisDashboard {
  kpis: {
    stores: number;
    itemsTracked: number;
    unitsOnHand: number;
    stockValue: number;
    lowStock: number;
    outOfStock: number;
    unitsSold: number;
    saleAmount: number;
    groups: number;
  };
  perGroup: {
    group: string;
    stores: number;
    unitsOnHand: number;
    stockValue: number;
    unitsSold: number;
    saleAmount: number;
    lowStock: number;
    outOfStock: number;
  }[];
  perStore: {
    partyId: number;
    storeName: string;
    city: string;
    group: string;
    itemsTracked: number;
    unitsOnHand: number;
    stockValue: number;
    lowStock: number;
    outOfStock: number;
    unitsSold: number;
    saleAmount: number;
  }[];
}

export async function getSisDashboard(filter: { from?: string; to?: string } = {}): Promise<SisDashboard> {
  const r = await api.get<SisDashboard>('/sis-stock/dashboard', {
    params: {
      ...(filter.from ? { from: filter.from } : {}),
      ...(filter.to ? { to: filter.to } : {}),
    },
  });
  return r.data;
}

/** Set a per-store reorder level for one SIS stock line. */
export async function setSisReorder(payload: {
  partyId: number;
  itemId: number;
  reorderLevel: number;
}): Promise<{ partyId: number; itemId: number; reorderLevel: number }> {
  const r = await api.post('/sis-stock/set-reorder', payload);
  return r.data;
}

/** Report shapes: units SOLD (subtract) vs remaining CLOSING stock (set). */
export type SisReportMode = 'sold' | 'closing';

/** Path for the sales-report Excel template download. `group` adds a Store column. */
export function sisSalesReportTemplatePath(mode: SisReportMode = 'sold', group = false): string {
  return `/sis-stock/sales-report/template.xlsx?mode=${mode}${group ? '&group=1' : ''}`;
}

/** Path for the uncapped SIS-stock Excel export, honouring the current filters. */
export function sisStockExportPath(filter: { partyId?: number; group?: string; search?: string } = {}): string {
  const q = new URLSearchParams();
  if (filter.partyId) q.set('partyId', String(filter.partyId));
  if (filter.group) q.set('group', filter.group);
  if (filter.search?.trim()) q.set('search', filter.search.trim());
  const qs = q.toString();
  return `/sis-stock/export.xlsx${qs ? `?${qs}` : ''}`;
}

type SisImportResult = ImportResult & { batchNo: string; duplicates: number; exceptions: number };

/** A sales-report line that would take a store's stock below zero (upload stopped, nothing saved). */
export interface OversoldLine { row: number; store: string; barcode: string; itemCode: string; billNo: string; onHand: number; qty: number; after: number }

/** Rows saved so far while a sales report uploads. */
export type SisImportProgress = (done: number, total: number) => void;

interface SisImportJob { status: 'running' | 'done' | 'failed'; total: number; done: number; result?: SisImportResult; error?: { status: number; body: unknown } }

/**
 * Upload in the background: the server answers once the file passes its checks
 * (Oversold, a busy store or a bad file still come back as errors straight away),
 * then this polls the upload until it is saved — no browser time-out on big files.
 * A failure while saving is thrown shaped like an HTTP error, so callers handle it
 * the same way.
 */
async function uploadSalesReport(query: string, file: File, onProgress?: SisImportProgress): Promise<SisImportResult> {
  const fd = new FormData();
  fd.append('file', file);
  const start = await api.post<{ jobId: string; total: number }>(`/sis-stock/sales-report/import?${query}&background=1`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 2 * 60 * 1000,
  });
  const { jobId, total } = start.data;
  onProgress?.(0, total);
  for (;;) {
    await new Promise((r) => setTimeout(r, 1000));
    const j = (await api.get<SisImportJob>(`/sis-stock/sales-report/import/jobs/${jobId}`)).data;
    onProgress?.(j.done, j.total);
    if (j.status === 'done') return j.result as SisImportResult;
    if (j.status === 'failed') {
      const body = (j.error?.body ?? {}) as { message?: string };
      throw Object.assign(new Error(body.message ?? 'The upload failed'), { response: { status: j.error?.status ?? 500, data: body } });
    }
  }
}

/** Upload one SIS store's report → updates only that store's stock. */
export async function importSisSalesReport(
  partyId: number,
  file: File,
  mode: SisReportMode = 'sold',
  allowOversold = false,
  onProgress?: SisImportProgress,
): Promise<SisImportResult> {
  return uploadSalesReport(`partyId=${partyId}&mode=${mode}${allowOversold ? '&allowOversold=1' : ''}`, file, onProgress);
}

/** Upload a group-wise report (one file, many stores; Store column per row). */
export async function importSisGroupReport(
  group: string,
  file: File,
  mode: SisReportMode = 'sold',
  allowOversold = false,
  onProgress?: SisImportProgress,
): Promise<SisImportResult> {
  return uploadSalesReport(`group=${encodeURIComponent(group)}&mode=${mode}${allowOversold ? '&allowOversold=1' : ''}`, file, onProgress);
}

export interface SisSalesBatch {
  batchNo: string;
  firstDate: string;
  lastDate: string;
  lines: number;
  units: number;
  netValue: number;
  storeLabel: string;
  group: string | null;
  isLatest: boolean;
}

export async function listSisSalesBatches(filter: { partyId?: number; group?: string } = {}): Promise<SisSalesBatch[]> {
  const r = await api.get<SisSalesBatch[]>('/sis-stock/sales-report/batches', {
    params: {
      ...(filter.partyId ? { partyId: filter.partyId } : {}),
      ...(filter.group ? { group: filter.group } : {}),
    },
  });
  return r.data;
}

export async function deleteSisSalesBatch(batchNo: string): Promise<{ batchNo: string; deletedLines: number; ledgerRows: number }> {
  const r = await api.post('/sis-stock/sales-report/batches/delete', { batchNo });
  return r.data;
}

/** SIS store + its partner feed label(s), for the group-upload setup modal. */
export interface SisStoreLabel {
  partyId: number;
  name: string;
  code: string;
  city: string;
  group: string;
  reportLabel: string;
}

export async function listSisStoreLabels(group?: string): Promise<SisStoreLabel[]> {
  const r = await api.get<SisStoreLabel[]>('/sis-stock/store-labels', { params: group ? { group } : {} });
  return r.data;
}

export async function saveSisStoreLabels(
  items: { partyId: number; reportLabel: string }[],
): Promise<{ updated: number }> {
  const r = await api.post('/sis-stock/store-labels', { items });
  return r.data;
}

export interface SisException {
  id: string;
  raisedAt: string;
  category: string;
  severity: string;
  partyId: number | null;
  storeName: string;
  itemId: number | null;
  itemCode: string;
  itemName: string;
  batchNo: string | null;
  rowRef: string | null;
  message: string;
  status: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
}

export interface SisExceptionSummary {
  open: number;
  resolved: number;
  byCategory: { category: string; count: number }[];
}

export async function listSisExceptions(filter: {
  status?: string;
  category?: string;
  partyId?: number;
  from?: string;
  to?: string;
} = {}): Promise<SisException[]> {
  const r = await api.get<SisException[]>('/sis-stock/exceptions', { params: filter });
  return r.data;
}

export async function getSisExceptionsSummary(): Promise<SisExceptionSummary> {
  const r = await api.get<SisExceptionSummary>('/sis-stock/exceptions/summary');
  return r.data;
}

export async function resolveSisException(id: string): Promise<{ id: string; status: string }> {
  const r = await api.post(`/sis-stock/exceptions/${id}/resolve`);
  return r.data;
}
